import { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { OversightTable, LicenseCard, CarouselNav, CarouselDots, Tag, type OversightColumn } from '@/components/ds';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSectionHeader } from '@/hooks/useSectionHeaders';
import { RenderHeaderParts } from '@/components/ds/RenderCMSHeader';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { useCarouselTracking } from '@/hooks/useCarouselTracking';
import { useContentHover } from '@/hooks/useContentHover';
import { mergeRefs } from '@/utils/mergeRefs';
import { useActiveLicenceEntities } from '@/hooks/useLicenceEntities';
import { useDisclaimer } from '@/hooks/useDisclaimers';

/**
 * Governance Section (About page)
 * 
 * CMS Integration:
 * - Uses useSectionHeader('about', 'governance') for header/tag/subheader
 */

// ⚠️ ANALYTICS: Card hover wrapper for license cards
const LicenseCardWrapper = ({ item, index, children }: {
  item: { regNo: string; entity: string };
  index: number;
  children: React.ReactNode;
}) => {
  const { contentHoverProps } = useContentHover({
    contentId: item.regNo,
    contentType: 'card',
    contentTitle: item.entity,
    contentSection: 'governance-licenses',
    contentPage: 'about',
    contentPosition: index,
  });

  return <div {...contentHoverProps}>{children}</div>;
};

interface License {
  entity: string;
  regNo: string;
  licenses: string[];
}

const oversightColumns: OversightColumn[] = [
  {
    title: 'Board Oversight',
    items: [
      'Group Board',
      'Executive Committee',
      'Audit and Risk Committee',
      'Investment Committee',
    ],
  },
  {
    title: 'Credit & Risk Management',
    items: [
      'Credit and Loss Control',
      'Credit Risk Management',
      'Asset Management Committees',
      'External Auditors',
    ],
  },
  {
    title: 'Regulatory and Statutory',
    items: [
      'FSCA',
      'Prudential Authority',
      'NCA',
    ],
  },
];

// licenseData is now derived from CMS in the component below

const GOVERNANCE_DISCLAIMER_FALLBACK =
  'The licences listed above are held by subsidiaries of Fedgroup Financial Holdings (Pty) Ltd and authorise the provision of regulated financial services by those entities. The tokenised investment offering contemplated by Fedgroup Private Markets is not governed by these licences and will be facilitated through a licensed third-party Crypto Asset Service Provider (CASP) partner.';

const GovernanceLicenceDisclaimer = () => {
  const { data: disclaimer } = useDisclaimer('governance-licence-disclaimer');
  const body = disclaimer?.body || GOVERNANCE_DISCLAIMER_FALLBACK;

  return (
    <div className="pl-4 md:pl-8 lg:pl-[max(2rem,calc((100vw-1400px)/2+2rem))] pr-4 md:pr-8 mt-8 max-w-[1400px]">
      {body.split('\n\n').map((para, i) => (
        <p key={i} className="text-[10px] text-graphite/60 leading-relaxed mb-2">{para}</p>
      ))}
    </div>
  );
};

const licenseTooltips: Record<string, string> = {
  'FAIS': 'Financial Advisory and Intermediary Services Act – authorises provision of financial advice and intermediary services.',

  '13B': 'Section 13B of the Pension Funds Act – authorises administration of pension fund benefits.',
  'CIS': 'Collective Investment Schemes – authorises management of pooled investment vehicles.',
  'FSP': 'Financial Services Provider – authorised to render financial services under the FAIS Act.',
  'Controlling Company': 'Licensed holding company under the Financial Sector Regulation Act.',
};

export default function GovernanceSection() {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isMobile = useIsMobile();
  
  // CMS data
  const sectionHeader = useSectionHeader('about', 'governance');
  const { data: cmsEntities } = useActiveLicenceEntities();

  // Map CMS data to existing License interface shape
  const licenseData: License[] = (cmsEntities || []).map(e => ({
    entity: e.entity_name,
    regNo: e.registration_number,
    licenses: e.licences,
  }));

  // ⚠️ ANALYTICS: Section visibility tracking
  const { ref: sectionRef, inView: sectionInView } = useSectionVisibility({
    sectionId: 'governance',
    sectionName: 'Governance',
    pageName: 'About',
    position: 6,
  });

  // ⚠️ ANALYTICS: Content hover tracking
  const { contentViewRef: governanceViewRef, handleMouseEnter: governanceMouseEnter, handleMouseLeave: governanceMouseLeave } = useContentHover({
    contentId: 'governance-accountability',
    contentType: 'content',
    contentTitle: 'Governance and Accountability',
    contentSection: 'governance',
    contentPage: 'about',
    minHoverDuration: 2000,
  });

  // ⚠️ ANALYTICS: License carousel tracking
  const {
    trackCarouselViewed,
    trackCarouselScrolled,
  } = useCarouselTracking({
    carouselId: 'governance-licenses',
    section: 'governance',
    pageName: 'About',
    totalItems: licenseData.length,
  });
  
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();

    // ⚠️ ANALYTICS: Track carousel scroll
    if (newIndex !== selectedIndex) {
      trackCarouselScrolled(selectedIndex, newIndex, 'drag');
    }

    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(newIndex);
  }, [emblaApi, selectedIndex, trackCarouselScrolled]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // ⚠️ ANALYTICS: Track carousel view when section is visible
  useEffect(() => {
    if (sectionInView && emblaApi && licenseData.length > 0) {
      const visibleCount = emblaApi.slidesInView().length;
      trackCarouselViewed(visibleCount);
    }
  }, [sectionInView, emblaApi, trackCarouselViewed, licenseData.length]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollToIndex = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  return (
    <TooltipProvider delayDuration={200}>
      <section id="governance" ref={mergeRefs(sectionRef, governanceViewRef)} className="py-24 md:py-32 bg-background overflow-hidden" onMouseEnter={governanceMouseEnter} onMouseLeave={governanceMouseLeave}>
        <div className="container mx-auto px-4 md:px-8">
          {/* molecule-section-header-with-tag */}
          <div className="mb-16">
            {/* atom-tag-subtle */}
            {sectionHeader?.tag_text && (
              <div className="mb-6">
                <Tag variant="subtle">{sectionHeader.tag_text}</Tag>
              </div>
            )}
            {/* atom-text-section-header + atom-text-section-header-emphasis */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-foreground mb-6 max-w-3xl">
              <RenderHeaderParts text={sectionHeader?.header_text || 'Built on **accountability.**'} />
            </h2>
            {/* atom-text-section-subheader */}
            {sectionHeader?.sub_header && (
              <p className="text-17 text-muted-foreground max-w-2xl">
                {sectionHeader.sub_header}
              </p>
            )}
          </div>

          {/* molecule-table-oversight */}
          <OversightTable columns={oversightColumns} className="mb-20" />

          {/* Licenses Block */}
          <div className="border-t border-border pt-12">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
              <div>
                {/* atom-text-section-header-emphasis (smaller variant) */}
                <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
                  Eight licences. One standard.
                </h3>
                {/* atom-text-section-subheader */}
                <p className="text-muted-foreground max-w-xl">
                  Fedgroup Financial Holdings is a licenced controlling company. Our subsidiaries 
                  are authorised across financial services, asset management, collective investments, 
                  and credit provision – all held to the same governance framework.
                </p>
              </div>
              {/* molecule-carousel-nav - desktop only */}
              {!isMobile && (
                <CarouselNav
                  onPrev={scrollPrev}
                  onNext={scrollNext}
                  canScrollPrev={canScrollPrev}
                  canScrollNext={canScrollNext}
                  variant="subdued"
                  className="self-start"
                />
              )}
            </div>
          </div>
        </div>

        {/* License Carousel - Full Width */}
        <div className="pl-4 md:pl-8 lg:pl-[max(2rem,calc((100vw-1400px)/2+2rem))]">
          <div className="overflow-visible" ref={emblaRef}>
            <div className="flex gap-4 embla-gesture-lock">
              {licenseData.map((item, index) => (
                <LicenseCardWrapper key={item.entity} item={item} index={index}>
                  <div
                    className={index === licenseData.length - 1
                      ? 'mr-4 md:mr-8 lg:mr-[max(2rem,calc((100vw-1400px)/2+2rem))]'
                      : ''
                    }
                  >
                    {/* component-card-license */}
                    <LicenseCard
                      entity={item.entity}
                      regNo={item.regNo}
                      licenses={item.licenses}
                      tooltips={licenseTooltips}
                    />
                  </div>
                </LicenseCardWrapper>
              ))}
            </div>
          </div>
        </div>

        {/* molecule-carousel-dots - mobile only */}
        {isMobile && (
          <div className="mt-6 px-4">
            <CarouselDots
              selectedIndex={selectedIndex}
              totalSlides={licenseData.length}
              onDotClick={scrollToIndex}
            />
          </div>
        )}

        {/* Governance licence disclaimer — CMS-managed */}
        <GovernanceLicenceDisclaimer />
      </section>
    </TooltipProvider>
  );
}
